/**
 * Browser-and-server-safe calendar helpers: iCalendar (.ics) text and Google
 * Calendar template links. No Supabase or Node imports so both the public
 * route handlers and the client UI can use them.
 */

export type CalendarEvent = {
  id: string;
  title: string;
  slug: string;
  description?: string | null;
  location?: string | null;
  starts_at: string;
  ends_at?: string | null;
  status?: string | null;
};

const DEFAULT_DURATION_MS = 3 * 60 * 60 * 1000;

export function eventEnd(event: CalendarEvent): Date {
  if (event.ends_at) {
    const end = new Date(event.ends_at);
    if (!Number.isNaN(end.getTime())) return end;
  }
  return new Date(new Date(event.starts_at).getTime() + DEFAULT_DURATION_MS);
}

/** UTC basic format: 20260902T203500Z */
export function toIcsStamp(value: Date | string): string {
  const d = typeof value === "string" ? new Date(value) : value;
  return `${d.toISOString().replace(/[-:]/g, "").split(".")[0]}Z`;
}

function escapeText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/** RFC 5545 requires lines under 75 octets; fold with a leading space. */
function fold(line: string): string {
  if (line.length <= 73) return line;
  const parts: string[] = [];
  let rest = line;
  while (rest.length > 73) {
    parts.push(rest.slice(0, 73));
    rest = rest.slice(73);
  }
  parts.push(rest);
  return parts.join("\r\n ");
}

export function eventUrl(origin: string, slug: string): string {
  return `${origin.replace(/\/$/, "")}/events/${slug}`;
}

export function buildIcs(
  events: CalendarEvent[],
  options: { origin: string; calendarName?: string },
): string {
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//SquadFlight FPV//Events//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-CALNAME:${escapeText(options.calendarName ?? "SquadFlight FPV")}`,
    "X-PUBLISHED-TTL:PT1H",
  ];

  for (const event of events) {
    const url = eventUrl(options.origin, event.slug);
    const description = [event.description?.trim(), url].filter(Boolean).join("\n\n");
    lines.push(
      "BEGIN:VEVENT",
      `UID:${event.id}@squadflight`,
      `DTSTAMP:${toIcsStamp(new Date())}`,
      `DTSTART:${toIcsStamp(event.starts_at)}`,
      `DTEND:${toIcsStamp(eventEnd(event))}`,
      fold(`SUMMARY:${escapeText(event.title)}`),
      fold(`DESCRIPTION:${escapeText(description)}`),
      fold(`URL:${url}`),
      `STATUS:${event.status === "cancelled" ? "CANCELLED" : "CONFIRMED"}`,
    );
    if (event.location) lines.push(fold(`LOCATION:${escapeText(event.location)}`));
    lines.push("END:VEVENT");
  }

  lines.push("END:VCALENDAR");
  return `${lines.join("\r\n")}\r\n`;
}

export function googleCalendarUrl(event: CalendarEvent, origin: string): string {
  const url = eventUrl(origin, event.slug);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${toIcsStamp(event.starts_at)}/${toIcsStamp(eventEnd(event))}`,
    details: [event.description?.trim(), url].filter(Boolean).join("\n\n"),
  });
  if (event.location) params.set("location", event.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Single-event .ics download link served by the public API route. */
export function icsDownloadUrl(slug: string): string {
  return `/api/public/event-ics?slug=${encodeURIComponent(slug)}`;
}

/** Subscribable feed of one host's live events. */
export function hostFeedUrl(origin: string, token: string): string {
  return `${origin.replace(/\/$/, "")}/api/public/host-calendar?token=${encodeURIComponent(token)}`;
}
